import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Wallet, TrendingUp, TrendingDown, Receipt } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import localApiAdapter from '../../core/services/localApiAdapter';
import ModuleAnalyticsSummaryPage from '../../shared/components/analytics/ModuleAnalyticsSummaryPage';
import {
  formatTreasuryAmount,
  formatTreasuryExportRows,
  getTreasuryExportCurrencyCode,
} from '../../shared/utils/cashTreasuryHelpers';
import { formatChartPeriodLabel } from '../../shared/utils/chartFormatters';
import { useSaudiDateFilter } from '../../products/saudi/context/SaudiDateFilterContext';
import { monthsCountForRange } from '../../products/saudi/utils/dateRangePresets';

const DEFAULT_DATA = {
  caisses: { totalCaisses: 0, activeCaisses: 0, totalBalance: 0 },
  month: { totalOperations: 0, totalReceipts: 0, totalPayments: 0 },
  balanceByCaisse: [],
  monthlyHistory: [],
};

const num = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const loadPosition = () =>
  localApiAdapter
    .get('/treasury/position/by-type/caisse')
    .catch(() => localApiAdapter.get('/tresorerie/position/by-type/caisse'))
    .catch(() => null);

const TreasuryAnalysesRapportsPage = () => {
  const { t } = useTranslation();
  const p = (key, opts) => t(`modules.caisse.pages.treasuryAnalysesRapportsPage.${key}`, opts);
  const { range } = useSaudiDateFilter();

  const [report, setReport] = useState(DEFAULT_DATA);
  const [loading, setLoading] = useState(true);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const formatAmount = formatTreasuryAmount;

  const loadReport = useCallback(async () => {
    setLoading(true);
    try {
      const months = monthsCountForRange(range);
      const [position, history, summary] = await Promise.all([
        loadPosition(),
        localApiAdapter.get('/treasury/monthly/history', { months }).catch(() => []),
        localApiAdapter.get('/treasury/monthly/summary').catch(() => null),
      ]);

      const accounts = position?.accounts || [];
      const activeCount = accounts.filter((row) => row.status === 'active').length;
      const summaryTotals = summary?.totals || {};

      setReport({
        ...DEFAULT_DATA,
        caisses: {
          totalCaisses: position?.accountsCount ?? accounts.length,
          activeCaisses: activeCount || accounts.length,
          totalBalance:
            num(position?.totalBalance) ||
            accounts.reduce((sum, row) => sum + num(row.balance), 0),
        },
        balanceByCaisse: accounts,
        monthlyHistory: Array.isArray(history) ? history : [],
        month: {
          totalReceipts: num(summaryTotals.credits),
          totalPayments: num(summaryTotals.debits),
          totalOperations: summary?.operations?.length || 0,
        },
      });
    } catch (error) {
      console.error('Treasury analyses load error:', error);
      setReport(DEFAULT_DATA);
    } finally {
      setLoading(false);
    }
  }, [range]);

  useEffect(() => {
    loadReport();
  }, [loadReport, refreshTrigger]);

  const periodTotals = useMemo(() => {
    const rows = report.monthlyHistory || [];
    if (rows.length) {
      return rows.reduce(
        (acc, row) => ({
          totalReceipts: acc.totalReceipts + num(row.totalCredits ?? row.receipts),
          totalPayments: acc.totalPayments + num(row.totalDebits ?? row.payments),
        }),
        { totalReceipts: 0, totalPayments: 0 },
      );
    }
    const month = report.month || DEFAULT_DATA.month;
    return {
      totalReceipts: num(month.totalReceipts),
      totalPayments: num(month.totalPayments),
    };
  }, [report]);

  const { totalReceipts, totalPayments } = periodTotals;
  const netFlow = totalReceipts - totalPayments;
  const caisses = report.caisses || DEFAULT_DATA.caisses;

  const receiptShare =
    totalReceipts + totalPayments > 0
      ? Math.round((totalReceipts / (totalReceipts + totalPayments)) * 100)
      : 0;

  const insights = useMemo(
    () => [
      {
        label: p('insight_net_flow'),
        value: loading ? '…' : formatAmount(netFlow),
        hint: p('insight_net_flow_hint'),
      },
      {
        label: p('insight_receipt_ratio'),
        value: loading ? '…' : `${receiptShare}%`,
        hint: p('insight_receipt_ratio_hint'),
      },
      {
        label: p('insight_caisse_count'),
        value: loading ? '…' : String(caisses.activeCaisses || caisses.totalCaisses || 0),
        hint: p('insight_caisse_count_hint'),
      },
    ],
    [loading, netFlow, receiptShare, caisses, p, formatAmount],
  );

  const stats = useMemo(
    () => [
      { label: p('stat_balance'), value: loading ? '…' : formatAmount(caisses.totalBalance || 0), icon: Wallet },
      { label: p('stat_receipts'), value: loading ? '…' : formatAmount(totalReceipts), icon: TrendingUp },
      { label: p('stat_payments'), value: loading ? '…' : formatAmount(totalPayments), icon: TrendingDown },
      { label: p('stat_net_flow'), value: loading ? '…' : formatAmount(netFlow), icon: Receipt },
    ],
    [loading, caisses, totalReceipts, totalPayments, netFlow, p, formatAmount],
  );

  const charts = useMemo(() => {
    const historyRows = [...(report.monthlyHistory || [])].reverse();
    const sliceCount = monthsCountForRange(range);

    const cashFlowRows = historyRows.slice(-sliceCount).map((row) => {
      const flow = num(row.solde ?? (num(row.totalCredits ?? row.receipts) - num(row.totalDebits ?? row.payments)));
      return {
        label: formatChartPeriodLabel(row.month || row.date, p, { fallback: p('na') }),
        value: Math.abs(flow),
        display: formatAmount(flow),
      };
    });

    const balanceRows = [...(report.balanceByCaisse || [])]
      .sort((a, b) => num(b.balance) - num(a.balance))
      .slice(0, 8)
      .map((row) => ({
        label: row.name || row.code || p('na'),
        value: Math.abs(num(row.balance)),
        display: formatAmount(row.balance),
      }));

    return [
      {
        title: p('chart_cashflow'),
        subtitle: p('chart_cashflow_subtitle'),
        rows: cashFlowRows,
      },
      {
        title: p('chart_balance_ranked'),
        subtitle: p('chart_balance_subtitle'),
        variant: 'ranked',
        rows: balanceRows.length ? balanceRows : [{ label: p('na'), value: 0, display: '—' }],
      },
      {
        title: p('chart_inflow_outflow'),
        subtitle: p('chart_inflow_subtitle'),
        variant: 'status',
        active: Math.round(totalReceipts),
        inactive: Math.round(totalPayments),
      },
    ];
  }, [report, range, totalReceipts, totalPayments, p, formatAmount]);

  const table = useMemo(() => {
    const rows = formatTreasuryExportRows(
      (report.balanceByCaisse || []).map((row) => ({
        name: row.name,
        code: row.code,
        type: row.type,
        balance: row.balance,
        currency: row.currency,
        status: row.status,
      })),
    ).map((row) => ({
      name: row.name || p('na'),
      code: row.code || p('na'),
      type: row.type || p('na'),
      balance: row.balance ?? '—',
      currency: row.currency || getTreasuryExportCurrencyCode(),
      status: row.status || p('na'),
    }));

    return {
      title: p('table_balances'),
      columns: [
        { key: 'name', label: p('col_caisse') },
        { key: 'code', label: p('col_code') },
        { key: 'type', label: p('col_type') },
        { key: 'balance', label: p('col_balance') },
        { key: 'currency', label: p('col_currency') },
        { key: 'status', label: p('col_status') },
      ],
      rows,
    };
  }, [report, p]);

  return (
    <ModuleAnalyticsSummaryPage
      title={p('title')}
      subtitle={p('subtitle')}
      loading={loading}
      onRefresh={() => setRefreshTrigger((n) => n + 1)}
      stats={stats}
      insights={insights}
      charts={charts}
      table={table}
      exportFilePrefix="treasury_analyses"
      emptyLabel={p('no_data')}
    />
  );
};

export default TreasuryAnalysesRapportsPage;
